import { apiClient, ApiResult } from "@/lib/api-client"
import { isAxiosError, type AxiosProgressEvent } from "axios"
import { EmployeeOnboardingDetailsDto } from "../employee/dto/employeeDetails"
import {
  DownloadMyDocumentResultDto,
  EmployeeDocumentsListDto,
  UploadMyDocumentResultDto,
} from "../employee/dto/employeeDocuments"
export type { DocumentToBeSignedDto } from "../employee/dto/employeeDetails"

export class EmployeeDocumentsApiError extends Error {
  errorCode?: string

  constructor(message: string, errorCode?: string) {
    super(message)
    this.name = "EmployeeDocumentsApiError"
    this.errorCode = errorCode
  }
}

// Maps enum integers to their string names if the API serialises enums as numbers.
const onboardingStatusMap: Record<number, string> = {
  [-1]: "Rejected",
  0: "Pending",
  1: "Started",
  2: "Submitted",
  3: "Approved",
  4: "Completed",
}

const employeeStatusMap: Record<number, string> = {
  [-1]: "Cancelled",
  0: "Pending",
  1: "Active",
}

// The backend returns RaceGroup as its enum name (e.g. "African") via ToString(),
// but the Race Group <Select> and submission payload use short codes (A/C/I/W).
// Without this conversion, the Select can't match "African" to any option and
// renders as unselected/blank after a fetch.
const raceGroupMap: Record<string, string> = {
  African: "A",
  Coloured: "C",
  Indian: "I",
  White: "W",
}

function normaliseDateOfBirth(dob: string | undefined | null): string {
  if (!dob) return ""
  // Already in yyyy-MM-dd format (HTML date input format)
  if (/^\d{4}-\d{2}-\d{2}$/.test(dob)) return dob
  // Handle yyyy/MM/dd format
  if (/^\d{4}\/\d{2}\/\d{2}$/.test(dob)) return dob.replace(/\//g, "-")
  // Handle ISO datetime format (e.g. 2000-01-15T00:00:00Z)
  const d = new Date(dob)
  if (!isNaN(d.getTime())) {
    return d.toISOString().split("T")[0]
  }
  return dob
}

function normaliseStatuses(
  data: EmployeeOnboardingDetailsDto,
): EmployeeOnboardingDetailsDto {
  return {
    ...data,
    dateOfBirth: normaliseDateOfBirth(data.dateOfBirth),
    onboardingStatus:
      typeof data.onboardingStatus === "number"
        ? (onboardingStatusMap[data.onboardingStatus as unknown as number] ??
          String(data.onboardingStatus))
        : data.onboardingStatus,
    employeeStatus:
      typeof data.employeeStatus === "number"
        ? (employeeStatusMap[data.employeeStatus as unknown as number] ??
          String(data.employeeStatus))
        : data.employeeStatus,
    raceGroup: data.raceGroup
      ? (raceGroupMap[data.raceGroup] ?? data.raceGroup)
      : data.raceGroup,
    address: (() => {
      if (!data.address || (data.address as unknown) === "null")
        return "" as unknown as typeof data.address
      if (typeof data.address === "string") {
        try {
          const p = JSON.parse(data.address)
          return {
            line1: p.Line1 ?? p.line1,
            line2: p.Line2 ?? p.line2,
            city: p.City ?? p.city,
            state: p.State ?? p.state,
            postalCode: p.PostalCode ?? p.postalCode,
            country: p.Country ?? p.country,
            complexNumber: p.ComplexNumber ?? p.complexNumber,
            complexName: p.ComplexName ?? p.complexName,
          }
        } catch {
          return "" as unknown as typeof data.address
        }
      }
      return data.address
    })(),
    bankDetails: (() => {
      if (!data.bankDetails || (data.bankDetails as unknown) === "null")
        return null as unknown as typeof data.bankDetails
      if (typeof data.bankDetails === "string") {
        try {
          const p = JSON.parse(data.bankDetails as unknown as string)
          return {
            bankName: p.BankName ?? p.bankName,
            accountNumber: p.AccountNumber ?? p.accountNumber,
            branchCode: p.BranchCode ?? p.branchCode,
            accountHolderName: p.AccountHolderName ?? p.accountHolderName,
            accountType: p.AccountType ?? p.accountType ?? "",
            accountRelationship: p.AccountRelationship ?? p.accountRelationship ?? "",
          }
        } catch {
          return null as unknown as typeof data.bankDetails
        }
      }
      return data.bankDetails
    })(),
  }
}

class OnboardingEmployee {
  private unwrapApiResult<T>(result: ApiResult<T>, fallbackMessage: string): T {
    if (!result.isSuccess) {
      throw new EmployeeDocumentsApiError(
        result.error ?? fallbackMessage,
        result.errorCode,
      )
    }

    return result.value
  }

  private mapEmployeeDocumentsError(
    error: unknown,
    fallbackMessage: string,
  ): EmployeeDocumentsApiError {
    if (error instanceof EmployeeDocumentsApiError) {
      return error
    }

    if (isAxiosError(error)) {
      const responseData = error.response?.data as
        | { error?: string; errorCode?: string }
        | undefined

      if (responseData?.error || responseData?.errorCode) {
        return new EmployeeDocumentsApiError(
          responseData.error ?? fallbackMessage,
          responseData.errorCode,
        )
      }

      return new EmployeeDocumentsApiError(error.message || fallbackMessage)
    }

    if (error instanceof Error) {
      return new EmployeeDocumentsApiError(error.message || fallbackMessage)
    }

    return new EmployeeDocumentsApiError(fallbackMessage)
  }

  async submitEmployee(formData: FormData): Promise<{
    id: string
    status: string
    submittedAt: string
    message: string
  }> {
    const response = await apiClient.post(`/Employee`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    return response.data
  }

  async fetchMyOnboardingData(): Promise<EmployeeOnboardingDetailsDto> {
    const response = await apiClient.get(`/onboarding/my-data`)
    const data = response.data
    // Backend sends flags as 'notes', map to 'fieldFlags' for the form
    const normalised = normaliseStatuses({
      ...data,
      fieldFlags: data.notes || data.fieldFlags || [],
    })
    return normalised
  }

  async fetchEmployee(): Promise<EmployeeOnboardingDetailsDto> {
    const response = await apiClient.get(`/onboarding/employees`)
    return response.data
  }

  async updateEmployeeOnboardingData(
    formData: FormData,
  ): Promise<EmployeeOnboardingDetailsDto> {
    const response = await apiClient.post(
      `/employee/update-onboarding`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    )
    return response.data
  }

  async fetchEmployeeById(id: string): Promise<EmployeeOnboardingDetailsDto> {
    const response = await apiClient.get(`/employee/employees/${id}`)
    return response.data
  }

  async fetchEmployeeAdminDetail(
    id: string,
  ): Promise<EmployeeOnboardingDetailsDto> {
    const response = await apiClient.get<
      ApiResult<EmployeeOnboardingDetailsDto>
    >(`/onboarding/employee/${id}`)
    const result = response.data
    if (!result.isSuccess) {
      throw new Error(result.error ?? "Failed to load employee details")
    }
    const val = result.value as unknown as Record<string, unknown>
    return normaliseStatuses({
      ...result.value,
      fieldFlags: (val.notes ||
        val.fieldFlags ||
        []) as EmployeeOnboardingDetailsDto["fieldFlags"],
    })
  }

  async approveEmployee(id: string): Promise<void> {
    const response = await apiClient.post<ApiResult<boolean>>(
      `/onboarding/employees/${id}/approve`,
      { employeeId: id },
    )
    const result = response.data
    if (!result.isSuccess) {
      throw new Error(result.error ?? "Failed to approve employee")
    }
  }

  async getMyDocumentDownloadUrl(
    documentId: string,
  ): Promise<EmployeeDocumentDownload> {
    const response = await apiClient.get(
      `/employee/my-documents/${documentId}/download`,
      { params: { _t: Date.now() } },
    )
    return response.data
  }

  async getMyDocuments(): Promise<EmployeeDocumentsListDto> {
    try {
      const response = await apiClient.get<ApiResult<EmployeeDocumentsListDto>>(
        `/employee/my-documents`,
        { params: { _t: Date.now() } },
      )

      return this.unwrapApiResult(response.data, "Failed to load documents")
    } catch (error) {
      throw this.mapEmployeeDocumentsError(error, "Failed to load documents")
    }
  }

  async uploadMyDocument(
    file: File,
    onUploadProgress?: (progressPercent: number) => void,
  ): Promise<UploadMyDocumentResultDto> {
    const formData = new FormData()
    formData.append("file", file)

    try {
      const response = await apiClient.post<
        ApiResult<UploadMyDocumentResultDto>
      >(`/employee/my-documents/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (!onUploadProgress) {
            return
          }

          const { loaded, total } = progressEvent
          if (!total || total <= 0) {
            return
          }

          const progressPercent = Math.min(
            100,
            Math.round((loaded / total) * 100),
          )
          onUploadProgress(progressPercent)
        },
      })

      return this.unwrapApiResult(response.data, "Failed to upload document")
    } catch (error) {
      throw this.mapEmployeeDocumentsError(error, "Failed to upload document")
    }
  }

  async deleteMyDocument(documentId: string): Promise<boolean> {
    try {
      const response = await apiClient.delete<ApiResult<boolean>>(
        `/employee/my-documents/${documentId}`,
      )

      return this.unwrapApiResult(response.data, "Failed to delete document")
    } catch (error) {
      throw this.mapEmployeeDocumentsError(error, "Failed to delete document")
    }
  }

  async getMyManagedDocumentDownloadUrl(
    documentId: string,
  ): Promise<DownloadMyDocumentResultDto> {
    try {
      const response = await apiClient.get<DownloadMyDocumentResultDto>(
        `/employee/my-documents/${documentId}/download`,
        { params: { _t: Date.now() } },
      )

      return response.data
    } catch (error) {
      throw this.mapEmployeeDocumentsError(error, "Failed to get download link")
    }
  }

  async getEmployeeDocumentDownloadUrl(
    employeeId: string,
    documentId: string,
  ): Promise<EmployeeDocumentDownload> {
    const response = await apiClient.get(
      `/onboarding/employees/${employeeId}/documents/${documentId}/download`,
      { params: { _t: Date.now() } },
    )
    return response.data
  }
}

export interface EmployeeDocumentDownload {
  downloadUrl: string
  fileName: string
  contentType: string
}

export const onboardingService = new OnboardingEmployee()
